import { Plus, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useLang } from "../../lib/i18n";
import { DateInput } from "./DateInput";
import { useFetch } from "../../lib/useFetch";

export const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>
);

const TextIn = ({ f, setF, k, label, type = "text", req, prefix = "" }) => (
  <Field label={label}>
    {type === "date" ? <DateInput required={req} value={f[k] || ""} onChange={(v) => setF({ ...f, [k]: v })} data-testid={`${prefix}${k.replace(/_/g, "-")}-input`} inputClassName="h-11 rounded-xl" />
      : <Input type={type} required={req} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${prefix}${k.replace(/_/g, "-")}-input`} className="h-11 rounded-xl" />}
  </Field>
);

export const SailorFields = ({ f, setF, prefix = "", child = false, holder = false }) => {
  const { t } = useLang();
  const p = { f, setF, prefix };
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <TextIn {...p} k="first_name" label={t("first_name")} req />
      <TextIn {...p} k="last_name" label={t("last_name")} req />
      <TextIn {...p} k="birth_date" label={t("birth_date")} type="date" req />
      <TextIn {...p} k="birth_place" label={t("birth_place")} />
      <TextIn {...p} k="tax_code" label={t("tax_code")} />
      {!child && <TextIn {...p} k="country" label={t("country")} />}
      {!child && <div className="md:col-span-2"><TextIn {...p} k="address" label={t("address")} /></div>}
      {holder && <TextIn {...p} k="city" label={t("city")} req />}
      {holder && <TextIn {...p} k="zip" label={t("zip")} req />}
      {!child && <TextIn {...p} k="phone" label={t("phone")} />}
    </div>
  );
};

export const GuardianFields = ({ g, setG, withEmail = false }) => {
  const { t } = useLang();
  const p = { f: g, setF: setG, prefix: "guardian-" };
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <TextIn {...p} k="first_name" label={t("first_name")} req />
      <TextIn {...p} k="last_name" label={t("last_name")} req />
      <TextIn {...p} k="tax_code" label={t("tax_code")} req />
      <TextIn {...p} k="birth_date" label={t("birth_date")} type="date" />
      <TextIn {...p} k="birth_place" label={t("birth_place")} />
      <TextIn {...p} k="phone" label={t("phone")} req />
      <div className="md:col-span-2"><TextIn {...p} k="address" label={t("address")} /></div>
      <TextIn {...p} k="city" label={t("city")} req />
      <TextIn {...p} k="zip" label={t("zip")} req />
      {withEmail && <TextIn {...p} k="email" label={t("email")} type="email" />}
    </div>
  );
};

export const ChipSelect = ({ value = [], onChange, testid }) => {
  const { data } = useFetch("/public/classes", { cached: true });
  return (
    <div className="flex flex-wrap gap-2">
      {(data || []).map((c) => {
        const on = value.includes(c.name);
        return (
          <button type="button" key={c.id} data-testid={`${testid}-${c.name}`} onClick={() => onChange(on ? value.filter((x) => x !== c.name) : [...value, c.name])}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${on ? "border-[#00A8B5] bg-[#00A8B5] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-[#00A8B5]"}`}>{c.name}</button>
        );
      })}
    </div>
  );
};

export const SportFields = ({ f, setF }) => {
  const { t } = useLang();
  const boats = f.boats || [];
  const setBoat = (i, k, v) => setF({ ...f, boats: boats.map((b, j) => (j === i ? { ...b, [k]: v } : b)) });
  return (
    <div className="space-y-6">
      <Field label={t("classes_practiced")}><ChipSelect value={f.classes} onChange={(v) => setF({ ...f, classes: v })} testid="classes-chip" /></Field>
      <Field label={t("boats")}>
        <div className="space-y-2">
          {boats.map((b, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Class" value={b.class_name} onChange={(e) => setBoat(i, "class_name", e.target.value)} data-testid={`boat-class-${i}`} />
              <Input placeholder={t("sail_number")} value={b.sail_number} onChange={(e) => setBoat(i, "sail_number", e.target.value)} data-testid={`boat-sail-${i}`} />
              <button type="button" onClick={() => setF({ ...f, boats: boats.filter((_, j) => j !== i) })} className="px-2 text-slate-400 hover:text-rose-500"><Trash2 size={16} /></button>
            </div>
          ))}
          <button type="button" onClick={() => setF({ ...f, boats: [...boats, { class_name: f.classes?.[0] || "", sail_number: "" }] })} className="flex items-center gap-1 text-sm font-semibold text-[#00A8B5]" data-testid="add-boat-btn"><Plus size={16} />{t("add_boat")}</button>
        </div>
      </Field>
      <Field label={t("sport_cv")}><Textarea rows={4} value={f.sport_cv || ""} onChange={(e) => setF({ ...f, sport_cv: e.target.value })} data-testid="sport-cv-input" /></Field>
      <Field label={t("interests")}><ChipSelect value={f.interest_classes} onChange={(v) => setF({ ...f, interest_classes: v })} testid="interest-chip" /></Field>
    </div>
  );
};

export const isMinor = (d) => {
  if (!d) return false;
  const b = new Date(d), n = new Date();
  let a = n.getFullYear() - b.getFullYear();
  if (n.getMonth() < b.getMonth() || (n.getMonth() === b.getMonth() && n.getDate() < b.getDate())) a--;
  return a < 18;
};
